/**
 * MARKIEREN, DANN KOMMENTIEREN ODER ABHAKEN (PO 18.09.2026: „wenn ich
 * textmarkiere, dann kann ich das auch als erledigt markieren: also
 * durchgestrichen und danach ein Häkchen").
 *
 * ══ DIE MARKIERUNG STEHT IM TEXT ═══════════════════════════════════════════
 * Beides wird als `<span>` IM gespeicherten HTML abgelegt — nicht als Offset
 * daneben. Ein Offset („Zeichen 40 bis 62") überlebt keine Bearbeitung: Wer
 * einen Satz davor ergänzt, verschöbe jede Markierung dahinter, und niemand
 * sähe es.
 *
 * Das Zeichen selbst (Haken, Sprechblase) kommt aus dem CSS (`::after`), nicht
 * aus dem Text: In der Datenbank stünde sonst ein SVG, das bei jeder
 * Änderung der Optik im Bestand zurückbliebe.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Eine erledigte Stelle: durchgestrichen, Haken dahinter. */
import { aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
export const KLASSE_GETAN = "nb-getan";
/** Eine kommentierte Stelle: hinterlegt, Sprechblase dahinter. */
export const KLASSE_ANMERK = "nb-anmerk";

/**
 * Der Schlüssel einer kommentierten Stelle. Er steht ZWEIMAL: im HTML der
 * Notiz (`data-anker`) und an jedem Kommentar dazu (Spalte `anker`) — gleich
 * heißt derselbe Faden.
 *
 * `[a-z0-9]{12}`, dieselbe Grenze wie im Reiniger und im Backend
 * (`sauberterAnker`). Kein `crypto.randomUUID`: Bindestriche müssten in einem
 * Attributselektor maskiert werden, und der Wert soll sich in einer
 * Fehlermeldung noch vorlesen lassen.
 */
export function neuerAnker(): string {
    const zufall =
        typeof crypto !== "undefined" && crypto.getRandomValues
            ? Array.from(crypto.getRandomValues(new Uint8Array(8)))
            : Array.from({ length: 8 }, () => Math.floor(Math.random() * 256));
    return zufall
        .map((b) => b.toString(36).padStart(2, "0"))
        .join("")
        .slice(0, 12);
}

/**
 * Die Markierung auf ganze Elemente ausdehnen, wo sie mitten in einem Chip
 * oder Bild endet.
 *
 * Ein `#`-Bezug ist ein `<span data-ref-art>` mit Text darin. Griffe die
 * Markierung nur die Hälfte davon, zerschnitte `extractContents` den Chip —
 * aus einer Mailkarte würden zwei halbe, und der Verweis wäre hin.
 */
export function aufGanzeChipsWeiten(range: Range): Range {
    const weit = range.cloneRange();
    const chip = (n: Node | null): Element | null => {
        const el = n?.nodeType === Node.ELEMENT_NODE ? (n as Element) : n?.parentElement ?? null;
        return el?.closest?.("[data-ref-art],img") ?? null;
    };
    const anfang = chip(weit.startContainer);
    if (anfang) weit.setStartBefore(anfang);
    const ende = chip(weit.endContainer);
    if (ende) weit.setEndAfter(ende);
    return weit;
}

/**
 * Den markierten Text umwickeln.
 *
 * `extractContents` + `insertNode` statt `surroundContents`: Letzteres WIRFT,
 * sobald die Markierung eine Elementgrenze schneidet („The Range has partially
 * selected a non-Text node") — und genau das ist der Normalfall, wenn jemand
 * quer durch ein `<b>` markiert.
 *
 * Reicht die Markierung über mehrere Absätze, entsteht JE ABSATZ ein `span`;
 * alle tragen denselben Anker, sind also ein Faden. Ein `span`, der zwei
 * Absätze umschlösse, wäre kein gültiges HTML und überlebte das Speichern
 * nicht.
 */
export function umwickle(
    range: Range,
    klasse: string,
    attribute: Record<string, string> = {},
): HTMLSpanElement[] {
    const weit = aufGanzeChipsWeiten(range);
    if (weit.collapsed) return [];
    const out: HTMLSpanElement[] = [];

    for (const teil of teileJeAbsatz(weit)) {
        if (teil.collapsed) continue;
        const span = document.createElement("span");
        span.className = klasse;
        for (const [k, v] of Object.entries(attribute)) span.setAttribute(k, v);
        try {
            span.appendChild(teil.extractContents());
            teil.insertNode(span);
        } catch {
            continue;
        }
        if (!(span.textContent ?? "").trim() && !span.querySelector("img")) {
            span.replaceWith(...Array.from(span.childNodes));
            continue;
        }
        out.push(span);
    }
    return out;
}

/**
 * Eine Markierung über mehrere Absätze in je einen Bereich JE ABSATZ zerlegen.
 * Bleibt sie in einem Absatz, kommt sie unverändert zurück.
 */
function teileJeAbsatz(range: Range): Range[] {
    const wurzel = range.commonAncestorContainer;
    const absatz = (n: Node): Element | null => {
        const el = n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement;
        return el?.closest?.("p,div,li,blockquote") ?? null;
    };
    const von = absatz(range.startContainer);
    const bis = absatz(range.endContainer);
    if (!von || von === bis) return [range];

    const hülle = wurzel.nodeType === Node.ELEMENT_NODE ? (wurzel as Element) : wurzel.parentElement;
    if (!hülle) return [range];
    const absaetze = Array.from(hülle.querySelectorAll("p,div,li,blockquote")).filter((a) =>
        range.intersectsNode(a),
    );
    if (!absaetze.length) return [range];

    const out: Range[] = [];
    for (const a of absaetze) {
        // Nur die BLATT-Absätze: ein `div`, das andere Absätze enthält, käme
        // sonst zusätzlich und umwickelte alles ein zweites Mal.
        if (a.querySelector("p,div,li,blockquote")) continue;
        const teil = aktivesDokument().createRange();
        teil.selectNodeContents(a);
        if (a.contains(range.startContainer) || a === range.startContainer) {
            teil.setStart(range.startContainer, range.startOffset);
        }
        if (a.contains(range.endContainer) || a === range.endContainer) {
            teil.setEnd(range.endContainer, range.endOffset);
        }
        out.push(teil);
    }
    return out.length ? out : [range];
}

/** Eine Markierung wieder auflösen — der Text bleibt, die Hülle geht. */
export function wickleAus(span: Element): void {
    span.replaceWith(...Array.from(span.childNodes));
}

/** Die markierte Stelle unter einem Klick- oder Zeigerziel; null = keine. */
export function stelleUnter(ziel: EventTarget | null): HTMLElement | null {
    const el = ziel as HTMLElement | null;
    return el?.closest?.(`.${KLASSE_ANMERK},.${KLASSE_GETAN}`) ?? null;
}
